/**
 * SummaryPage.jsx
 * KPI summary dashboard — the first thing you see when the app loads.
 * Shows key metrics, charts (product type revenue, top products, inventory health),
 * product type table, and reorder alerts.
 */

import React from 'react';
import {
  Layout,
  Card,
  DataTable,
  SkeletonBodyText,
  Banner,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  Divider,
} from '@shopify/polaris';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  PieChart,
  Pie,
} from 'recharts';

import { useReport } from '../hooks/useReport';
import { api } from '../api/api';
import {
  formatCurrency,
  formatNumber,
  formatDate,
  formatRunway,
  formatRate,
  periodLabel,
} from '../utils/formatters';

const CHART_FILL = '#008060';
const TYPE_CHART_HEIGHT = 280;
const TOP_PRODUCTS_HEIGHT = 320;
const PIE_HEIGHT = 320;
const TYPE_CHART_LIMIT = 8;
const TOP_PRODUCTS_LIMIT = 10;
const REORDER_ALERTS_LIMIT = 10;
const LABEL_MAX_LENGTH_TYPE = 16;
const LABEL_MAX_LENGTH_PRODUCT = 20;

const TYPE_CHART_COLORS = [
  '#008060', '#1DA462', '#95C9B4', '#CDEADF',
  '#004C3F', '#637381', '#B2BBC5', '#E4E5E7',
];

const HEALTH_COLORS = {
  Healthy: '#008060',
  'Low Stock': '#FFC453',
  Overstock: '#EEC200',
  'Dead Stock': '#D72C0D',
};

/**
 * A single KPI metric card with title, large value, and optional subtitle.
 *
 * @param {{ title: string, value: string, subtitle?: string, tone?: string }} props
 */
function MetricCard({ title, value, subtitle, tone }) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text variant="bodySm" tone="subdued" as="p">{title}</Text>
        <Text variant="heading2xl" as="p" tone={tone}>
          {value}
        </Text>
        {subtitle && (
          <Text variant="bodySm" tone="subdued" as="p">{subtitle}</Text>
        )}
      </BlockStack>
    </Card>
  );
}

export function SummaryPage({ period, overstockDays, reorderDays, filterBar, onNavigate }) {
  const { data, loading, error } = useReport(api.getSummary, {
    period,
    overstockDays,
  });

  const { data: typeData, loading: typeLoading } = useReport(api.getByType, {
    period,
    overstockDays,
    limit: TYPE_CHART_LIMIT,
  });

  const { data: topProductsData, loading: topProductsLoading } = useReport(api.getSales, {
    period,
    overstockDays,
    limit: TOP_PRODUCTS_LIMIT,
    sortBy: 'revenue',
    sortDir: 'desc',
  });

  const { data: reorderData, loading: reorderLoading } = useReport(api.getReorderAlerts, {
    period,
    reorderDays,
    limit: REORDER_ALERTS_LIMIT,
    sortBy: 'runwayDays',
    sortDir: 'asc',
  });

  if (error) {
    return (
      <Banner title="Failed to load summary" tone="critical">
        <p>{error}</p>
      </Banner>
    );
  }

  // Chart 0: Revenue by product type
  const typeChartData = typeData?.data?.slice(0, TYPE_CHART_LIMIT).map((t) => ({
    name: t.productType.length > LABEL_MAX_LENGTH_TYPE ? t.productType.slice(0, LABEL_MAX_LENGTH_TYPE) + '…' : t.productType,
    revenue: t.totalRevenue,
  })) || [];

  // Chart 1: Top 10 products by revenue (horizontal bar)
  const topProductsChartData = (topProductsData?.data || []).slice(0, TOP_PRODUCTS_LIMIT).map((r) => ({
    name: r.productTitle.length > LABEL_MAX_LENGTH_PRODUCT ? r.productTitle.slice(0, LABEL_MAX_LENGTH_PRODUCT) + '…' : r.productTitle,
    revenue: r.revenue,
  }));

  // Chart 2: Inventory health donut
  const healthData = [];
  if (data && !loading) {
    const deadStock = data.deadStockCount || 0;
    const overstock = data.overstockedCount || 0;
    const lowStock = data.lowStockCount || 0;
    const total = data.totalSkus || 0;
    const healthy = Math.max(0, total - deadStock - overstock - lowStock);

    if (total > 0) {
      healthData.push(
        { name: 'Healthy', value: healthy, fill: HEALTH_COLORS.Healthy },
        { name: 'Low Stock', value: lowStock, fill: HEALTH_COLORS['Low Stock'] },
        { name: 'Overstock', value: overstock, fill: HEALTH_COLORS.Overstock },
        { name: 'Dead Stock', value: deadStock, fill: HEALTH_COLORS['Dead Stock'] },
      );
    }
  }
  const healthTotal = healthData.reduce((s, d) => s + d.value, 0);

  return (
    <BlockStack gap="400">
      {/* Shared filter bar */}
      <Card>{filterBar}</Card>

      {/* Date range context */}
      {data && (
        <Text variant="bodySm" tone="subdued" as="p">
          {periodLabel(period)} · {formatDate(data.startDate)} – {formatDate(data.endDate)}
        </Text>
      )}

      {/* KPI row */}
      <Layout>
        <Layout.Section variant="oneThird">
          {loading ? (
            <Card><SkeletonBodyText lines={3} /></Card>
          ) : (
            <MetricCard
              title="Total Revenue"
              value={formatCurrency(data?.totalRevenue)}
              subtitle={`${formatNumber(data?.totalOrders)} orders`}
            />
          )}
        </Layout.Section>
        <Layout.Section variant="oneThird">
          {loading ? (
            <Card><SkeletonBodyText lines={3} /></Card>
          ) : (
            <MetricCard
              title="Units Sold"
              value={formatNumber(data?.totalUnitsSold)}
              subtitle={`Avg order: ${formatCurrency(data?.avgOrderValue)}`}
            />
          )}
        </Layout.Section>
        <Layout.Section variant="oneThird">
          {loading ? (
            <Card><SkeletonBodyText lines={3} /></Card>
          ) : (
            <MetricCard
              title="Active SKUs"
              value={formatNumber(data?.totalSkus)}
              subtitle={`${formatNumber(data?.deadStockCount)} dead stock`}
            />
          )}
        </Layout.Section>
      </Layout>

      {/* Alert row */}
      <Layout>
        <Layout.Section variant="oneHalf">
          {loading ? (
            <Card><SkeletonBodyText lines={2} /></Card>
          ) : (
            <MetricCard
              title="Overstock Value"
              value={formatCurrency(data?.totalOverstockValue)}
              subtitle={`${formatNumber(data?.overstockedCount)} SKUs overstocked`}
              tone={data?.overstockedCount > 0 ? 'caution' : undefined}
            />
          )}
        </Layout.Section>
        <Layout.Section variant="oneHalf">
          {loading ? (
            <Card><SkeletonBodyText lines={2} /></Card>
          ) : (
            <Card>
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued" as="p">Clearance Candidates</Text>
                <InlineStack gap="200" blockAlign="center">
                  <Text variant="heading2xl" as="p" tone="critical">
                    {formatNumber(data?.deadStockCount)}
                  </Text>
                  <Badge tone="critical">Needs action</Badge>
                </InlineStack>
                <Text variant="bodySm" tone="subdued" as="p">
                  SKUs with zero sales in the period
                </Text>
              </BlockStack>
            </Card>
          )}
        </Layout.Section>
      </Layout>

      <Divider />

      {/* Chart 0: Revenue by product type */}
      <Card>
        <BlockStack gap="300">
          <Text variant="headingMd" as="h2">Revenue by Product Type</Text>
          {typeLoading ? (
            <SkeletonBodyText lines={8} />
          ) : (
            <ResponsiveContainer width="100%" height={TYPE_CHART_HEIGHT}>
              <BarChart data={typeChartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E4E5E7" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fill: '#637381' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 12, fill: '#637381' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ borderRadius: 8, border: '1px solid #E4E5E7' }}
                />
                <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                  {typeChartData.map((_, i) => (
                    <Cell key={i} fill={TYPE_CHART_COLORS[i % TYPE_CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </BlockStack>
      </Card>

      {/* Product type table */}
      <Card>
        <BlockStack gap="300">
          <Text variant="headingMd" as="h2">Product Type Breakdown</Text>
          {typeLoading ? (
            <SkeletonBodyText lines={6} />
          ) : (
            <DataTable
              columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
              headings={['Product Type', 'Revenue', 'Units Sold', 'Variants', 'Dead Stock', 'Overstocked']}
              rows={
                typeData?.data?.map((t) => [
                  t.productType,
                  formatCurrency(t.totalRevenue),
                  formatNumber(t.totalUnitsSold),
                  formatNumber(t.variantCount),
                  t.deadStockCount > 0 ? (
                    <Badge tone="critical">{t.deadStockCount}</Badge>
                  ) : '0',
                  t.overstockedCount > 0 ? (
                    <Badge tone="attention">{t.overstockedCount}</Badge>
                  ) : '0',
                ]) || []
              }
            />
          )}
        </BlockStack>
      </Card>

      {/* Charts row: Top Products + Inventory Health */}
      <Layout>
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd" as="h2">Top 10 Products by Revenue</Text>
              {topProductsLoading ? (
                <SkeletonBodyText lines={10} />
              ) : topProductsChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={TOP_PRODUCTS_HEIGHT}>
                  <BarChart
                    data={topProductsChartData}
                    layout="vertical"
                    margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E4E5E7" />
                    <XAxis
                      type="number"
                      tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                      tick={{ fontSize: 12, fill: '#637381' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={140}
                      tick={{ fontSize: 11, fill: '#637381' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ borderRadius: 8, border: '1px solid #E4E5E7' }}
                    />
                    <Bar dataKey="revenue" fill={CHART_FILL} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Text variant="bodyMd" tone="subdued" as="p">No sales data for this period.</Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd" as="h2">Inventory Health</Text>
              {loading ? (
                <SkeletonBodyText lines={10} />
              ) : healthData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={PIE_HEIGHT - 80}>
                    <PieChart>
                      <Pie
                        data={healthData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                      >
                        {healthData.map((entry, i) => (
                          <Cell key={i} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, name) => [`${value} SKUs (${healthTotal > 0 ? ((value / healthTotal) * 100).toFixed(1) : 0}%)`, name]}
                        contentStyle={{ borderRadius: 8, border: '1px solid #E4E5E7' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <InlineStack gap="400" align="center" wrap>
                    {healthData.map((entry) => (
                      <InlineStack key={entry.name} gap="100" blockAlign="center">
                        <div style={{ width: 12, height: 12, borderRadius: 3, background: entry.fill }} />
                        <Text variant="bodySm" as="span">
                          {entry.name}: {formatNumber(entry.value)} ({healthTotal > 0 ? ((entry.value / healthTotal) * 100).toFixed(0) : 0}%)
                        </Text>
                      </InlineStack>
                    ))}
                  </InlineStack>
                </>
              ) : (
                <Text variant="bodyMd" tone="subdued" as="p">No inventory data available.</Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Reorder alerts */}
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap="200" blockAlign="center">
              <Text variant="headingMd" as="h2">Reorder Alerts</Text>
              {!reorderLoading && reorderData?.pagination?.total > 0 && (
                <Badge tone="critical">{reorderData.pagination.total} SKUs</Badge>
              )}
            </InlineStack>
            <Text variant="bodySm" tone="subdued" as="span">
              Runway &lt; {reorderDays} days
            </Text>
          </InlineStack>

          {reorderLoading ? (
            <SkeletonBodyText lines={6} />
          ) : reorderData?.data?.length > 0 ? (
            <>
              <DataTable
                columnContentTypes={['text', 'text', 'numeric', 'text', 'text']}
                headings={['SKU', 'Product', 'In Stock', 'Runway', 'Sell Rate']}
                rows={reorderData.data.map((r) => [
                  r.sku,
                  r.productTitle,
                  formatNumber(r.inventoryQuantity),
                  <Badge tone="critical">{formatRunway(r.runwayDays)}</Badge>,
                  formatRate(r.dailySellRate),
                ])}
              />
              {reorderData.pagination.total > REORDER_ALERTS_LIMIT && (
                <Button
                  variant="plain"
                  onClick={() => onNavigate && onNavigate('inventory')}
                >
                  View all {formatNumber(reorderData.pagination.total)} alerts →
                </Button>
              )}
            </>
          ) : (
            <Text variant="bodyMd" tone="subdued" as="p">
              No SKUs need reorder — all items have at least {reorderDays} days of runway.
            </Text>
          )}
        </BlockStack>
      </Card>
    </BlockStack>
  );
}
